import React, { useState, useRef, useEffect } from 'react';
import { Icon } from './icons/Icon';
import './Pagination.css';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../utils/language.utils';
import { useTranslation } from '../hooks/useTranslation';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: number[];
    language?: SupportedLanguage;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
    language = DEFAULT_LANGUAGE
}) => {
    const t = useTranslation(language);
    const [pageSizeOpen, setPageSizeOpen] = useState(false);
    const pageSizeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!pageSizeOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (pageSizeRef.current && !pageSizeRef.current.contains(e.target as Node)) {
                setPageSizeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [pageSizeOpen]);

    // Sayfalama metinlerini hesapla
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    const paginationRangeText = totalItems > 0
        ? `${startItem}-${endItem} / ${totalItems}`
        : '0-0 / 0';
    const paginationPageText = `${currentPage} / ${totalPages || 1}`;

    return (
        <div className="products-pagination">
            <div className="pagination-info">
                <span className="pagination-range">{paginationRangeText}</span>
                <div className="page-size-select-wrap" ref={pageSizeRef}>
                    <button
                        type="button"
                        className="pagination-page-size-select page-size-select"
                        aria-label={t.common.perPage}
                        aria-expanded={pageSizeOpen}
                        aria-haspopup="listbox"
                        onClick={() => setPageSizeOpen((o) => !o)}
                    >
                        <span className="page-size-select-value">{pageSize}</span>
                        <Icon name="expandMore" size={16} className="page-size-select-chevron" />
                    </button>
                    {pageSizeOpen && (
                        <ul
                            className="page-size-select-dropdown"
                            role="listbox"
                            aria-label={t.common.perPage}
                        >
                            {pageSizeOptions.map((option) => (
                                <li
                                    key={option}
                                    role="option"
                                    aria-selected={pageSize === option}
                                    className={`page-size-option ${pageSize === option ? 'selected' : ''}`}
                                    onClick={() => {
                                        onPageSizeChange(option);
                                        setPageSizeOpen(false);
                                    }}
                                >
                                    {option}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            <div className="pagination-controls">
                <button
                    className="pagination-btn"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    title={t.common.firstPage}
                >
                    <Icon name="chevronLeft" size={16} />
                    <Icon name="chevronLeft" size={16} />
                </button>
                <button
                    className="pagination-btn"
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    title={t.common.previousPage}
                >
                    <Icon name="chevronLeft" size={16} />
                </button>
                <span className="pagination-page">{paginationPageText}</span>
                <button
                    className="pagination-btn"
                    onClick={() => onPageChange(Math.min(totalPages || 1, currentPage + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    title={t.common.nextPage}
                >
                    <Icon name="chevronRight" size={16} />
                </button>
                <button
                    className="pagination-btn"
                    onClick={() => onPageChange(totalPages || 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    title={t.common.lastPage}
                >
                    <Icon name="chevronRight" size={16} />
                    <Icon name="chevronRight" size={16} />
                </button>
            </div>
        </div>
    );
};
